/**
 * useSSEHealth — tracks SSE stream liveness by listening for the
 * `sse:activity` custom event dispatched by `useSSE` on every chunk.
 *
 * State machine:
 *   connected  → initial state; any `sse:activity` event resets here
 *   silent     → no activity for `silentMs` (default 15 s)
 *   disconnected → no activity for `disconnectedMs` (default 30 s)
 *
 * The `sse:activity` event is global on `window`, so this hook works
 * even when `useSSE` is mounted in a sibling or ancestor component.
 *
 * SSR-safe: returns 'connected' on the server (assume live for hydration).
 */
import { useEffect, useRef, useState } from "react";
import { SSE_ACTIVITY_EVENT } from "#/hooks/useSSE.js";

export type SSEHealth = "connected" | "silent" | "disconnected";

export interface UseSSEHealthOptions {
  /** Ms of silence before transitioning to `silent`. Default 15 000. */
  silentMs?: number;
  /** Ms of silence before transitioning to `disconnected`. Default 30 000. */
  disconnectedMs?: number;
  /** Check interval (ms). Default 1 000. */
  checkIntervalMs?: number;
}

export function useSSEHealth(options: UseSSEHealthOptions = {}): SSEHealth {
  const { silentMs = 15_000, disconnectedMs = 30_000, checkIntervalMs = 1_000 } = options;

  const [health, setHealth] = useState<SSEHealth>("connected");
  const lastActivityRef = useRef<number>(Date.now());

  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleActivity = () => {
      lastActivityRef.current = Date.now();
      setHealth("connected");
    };

    window.addEventListener(SSE_ACTIVITY_EVENT, handleActivity);

    const interval = setInterval(() => {
      const elapsed = Date.now() - lastActivityRef.current;
      if (elapsed >= disconnectedMs) {
        setHealth("disconnected");
      } else if (elapsed >= silentMs) {
        setHealth("silent");
      } else {
        setHealth("connected");
      }
    }, checkIntervalMs);

    return () => {
      window.removeEventListener(SSE_ACTIVITY_EVENT, handleActivity);
      clearInterval(interval);
    };
  }, [silentMs, disconnectedMs, checkIntervalMs]);

  return health;
}
